/**
 * tsk-apps-review-19: plan pause of Apps, as the Studio sees it.
 *
 * A plan without Apps (App limit 0) keeps existing App Containers visible but
 * locks creating, editing and publishing them, whoever their pages are for;
 * the server pauses their delivery. The server stays authoritative; these
 * helpers only decide what the Studio shows.
 */
import type { useI18n } from '@affine/i18n';

import { hasAppBaseErrorCode } from './app-base-error-code';

export const APP_BASE_KIND_NOT_AVAILABLE = 'APP_BASE_KIND_NOT_AVAILABLE';
export const APP_BASE_LIMIT_REACHED = 'APP_BASE_LIMIT_REACHED';
export const APP_CONTAINER_PLAN_QUOTA_EXCEEDED =
  'APP_CONTAINER_PLAN_QUOTA_EXCEEDED';

/** Which count of the App Container is above its plan, as the server says. */
export interface AppContainerPlanQuotaDetail {
  readonly quota: 'tables' | 'agentActions';
  readonly used: number;
  readonly limit: number;
  readonly plan: string;
}

/** The count a plan quota conflict names; any other value is a contract break. */
export function appContainerPlanQuotaKind(
  value: unknown
): AppContainerPlanQuotaDetail['quota'] {
  if (value === 'tables' || value === 'agentActions') return value;
  throw new Error(`Unknown App plan quota: ${String(value)}`);
}

/** The plan quota detail of a refused growth; null for any other error. */
export function readAppContainerPlanQuota(
  error: unknown
): AppContainerPlanQuotaDetail | null {
  const graphQLErrors = (
    error as {
      graphQLErrors?: Array<{ extensions?: Record<string, unknown> }>;
    }
  )?.graphQLErrors;
  const extensions = graphQLErrors?.find(
    graphQLError =>
      graphQLError.extensions?.code === APP_CONTAINER_PLAN_QUOTA_EXCEEDED
  )?.extensions;
  // The server passes the detail of a domain error on as text.
  const detail = extensions?.data as Record<string, unknown> | undefined;
  const count = (value: unknown) =>
    typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : null;
  const used = count(detail?.used);
  const limit = count(detail?.limit);
  if (
    !detail ||
    (detail.quota !== 'tables' && detail.quota !== 'agentActions') ||
    used === null ||
    limit === null ||
    typeof detail.plan !== 'string'
  ) {
    return null;
  }
  return { quota: detail.quota, used, limit, plan: detail.plan };
}

/** One sentence: what the App Container needs, what its plan allows. */
export function appContainerPlanQuotaMessage(
  t: ReturnType<typeof useI18n>,
  detail: Omit<AppContainerPlanQuotaDetail, 'used' | 'limit'> & {
    readonly used: number | string;
    readonly limit: number | string;
  }
): string {
  const values = {
    used: String(detail.used),
    limit: String(detail.limit),
    plan: detail.plan,
  };
  return detail.quota === 'tables'
    ? t['com.streamlain.appBases.planQuota.tables'](values)
    : t['com.streamlain.appBases.planQuota.agentActions'](values);
}

/** Plan flag of the AppBase quota snapshot; null when the snapshot lacks it. */
export function readAppsAvailable(snapshot: unknown): boolean | null {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return null;
  }
  const value = (snapshot as Record<string, unknown>).appsAvailable;
  return typeof value === 'boolean' ? value : null;
}

/** Every App on a plan without Apps is visible but locked. */
export function isAppBasePlanLocked(
  appsAvailable: boolean | null | undefined
): boolean {
  return appsAvailable === false;
}

/** Mirrors the server check: the pause applies from its instant on. */
export function isAppBasePlanPausedAt(
  planPausedAt: string | null | undefined,
  now: number
): boolean {
  if (!planPausedAt) return false;
  const pausedAt = Date.parse(planPausedAt);
  return Number.isFinite(pausedAt) && pausedAt <= now;
}

/** The server refused Apps the workspace plan does not include. */
export function isAppsNotAvailableError(error: unknown): boolean {
  return hasAppBaseErrorCode(error, APP_BASE_KIND_NOT_AVAILABLE);
}

/** The workspace holds as many App Containers of a kind as its plan allows. */
export function isAppBaseLimitReachedError(error: unknown): boolean {
  return hasAppBaseErrorCode(error, APP_BASE_LIMIT_REACHED);
}

/**
 * What the workspace plan refused, in the words of the Studio; null for any
 * other error.
 */
export function appBasePlanRefusalMessage(
  t: ReturnType<typeof useI18n>,
  error: unknown
): string | null {
  if (isAppsNotAvailableError(error)) {
    return t['com.streamlain.appBases.planPause.locked']();
  }
  if (isAppBaseLimitReachedError(error)) {
    return t['com.streamlain.appBases.planPause.limitReached']();
  }
  const planQuota = readAppContainerPlanQuota(error);
  if (planQuota) return appContainerPlanQuotaMessage(t, planQuota);
  return null;
}
